import { fill, t } from '../strings';

/**
 * 🔒 A suite lives on the site, never on the building (row T3078, decision 7400).
 * A Location's address lines hold the street and the building's own parts (Tower,
 * Building, Wing, Floor), which every occupant shares; a suite, unit, apartment,
 * room or `#` part is one occupant's and belongs to `SiteRecord.suite`. This is the
 * one parser the Location write, the Add Site write, the import, an address
 * answer and the one-time move all read.
 *
 * 🔨 A word counts only when a suite identifier follows it: one to eight letters
 * or digits (with hyphens between groups) that holds a digit or is a single
 * letter. So `Suite 1600`, `Ste. 4B` and `Unit A` are suite parts, while
 * `Suiter Rd`, `Unit Rd` and a bare `Suite` are not. A period after the identifier
 * (`Suite 1600.`) is part of the line's end and goes with the part (T3086).
 */

/** The suite words, any case, with or without a period; `#` is read on its own below. */
const SUITE_WORD = '(?:suite|ste|unit|apt|room|rm)';
const IDENTIFIER = '[A-Za-z0-9]{1,8}(?:-[A-Za-z0-9]{1,8})*';
/**
 * A suite word stands as a whole word and is followed by a period, white space or a `#` before its identifier, so
 * `Executive Suites`, `Garden Apts`, `Corporate Unity` and `Main Stem` are never suites.
 */
const WORD_PART = new RegExp(`(?:^|[\\s,;])((${SUITE_WORD})(?:\\.\\s*|\\s+|(?=#))#?\\s*(${IDENTIFIER}))\\.?\\s*$`, 'i');
/** A `#` then an identifier, at the end of a line and after a start, a space or a comma. */
const HASH_PART = new RegExp(`(?:^|[\\s,;])(#\\s*(${IDENTIFIER}))\\.?\\s*$`);
/** A `#` after one of these numbers the building's own part, never a suite: `Tower #1`, `Building #5`, `Plant #2`. */
const BUILDING_WORD = /\b(?:tower|building|bldg|wing|plant|floor|flr|fl)\.?\s*$/i;

const holdsDigitOrIsOneLetter = (identifier: string): boolean => /\d/.test(identifier) || /^[A-Za-z]$/.test(identifier);

/** One suite part found at the end of one line: what is left of the line, the part as written, and the suite to hold. */
export interface SuiteInLine {
  /** The line without the part and the separator before it; may be empty. */
  rest: string;
  /** The part as it stood, for a sentence to name: `Suite 1600`. */
  part: string;
  /** What the site's Suite holds: the identifier alone for Suite, Ste and `#` (the page prints Suite before it), the word kept for Unit, Apt, Room and Rm. */
  suite: string;
}

/** A post office box number is not a suite: `PO Box #5` keeps its number. */
const POST_BOX = /\bbox\s*$/i;

/** The suite part a line ends in, or null; a street name that merely contains a suite word is not one. */
export function suiteInLine(line: string): SuiteInLine | null {
  const text = line.trim();
  const word = WORD_PART.exec(text);
  if (word && holdsDigitOrIsOneLetter(word[3]!)) {
    const rest = restBefore(text, word);
    if (POST_BOX.test(rest)) return null;
    const suiteWord = word[2]!.toLowerCase();
    const holds = suiteWord === 'suite' || suiteWord === 'ste' ? word[3]! : `${capitalise(suiteWord)} ${word[3]!}`;
    return { rest, part: word[1]!.trim(), suite: holds };
  }
  const hash = HASH_PART.exec(text);
  if (hash && holdsDigitOrIsOneLetter(hash[2]!)) {
    const rest = restBefore(text, hash);
    if (POST_BOX.test(rest) || BUILDING_WORD.test(rest)) return null;
    return { rest, part: hash[1]!.trim(), suite: hash[2]! };
  }
  return null;
}

/** The line before the matched part and the separator ahead of it. */
function restBefore(text: string, match: RegExpExecArray): string {
  return stripSeparator(text.slice(0, match.index + match[0].indexOf(match[1]!)));
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function stripSeparator(text: string): string {
  return text.replace(/[\s,;]+$/, '').trim();
}

/**
 * 🔒 A site's suite as a page prints it (T3086): the word Suite before an identifier alone, and the value as it stands where it begins
 * with its own word (`Unit 5`, `Apt 4B`), so a page never prints `Suite Unit 5`.
 */
export function suiteLabel(suite: string): string {
  return /^(?:unit|apt|room|rm)\b/i.test(suite.trim()) ? suite.trim() : `Suite ${suite.trim()}`;
}

/** What a set of address lines comes to once its suite parts are taken out. */
export interface SplitLines {
  /** The lines that stay on the Location, in order, a line left empty by the split dropped. */
  lines: string[];
  /** The suite parts found, as written, in order. */
  parts: string[];
  /** The suites to hold, one per part, in order. */
  suites: string[];
}

/** Takes the suite part off the end of each line; Tower, Building, Wing and Floor parts are left where they are. */
export function splitSuiteFromLines(lines: readonly string[]): SplitLines {
  const kept: string[] = [];
  const parts: string[] = [];
  const suites: string[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const found = suiteInLine(line);
    if (!found) {
      kept.push(line);
      continue;
    }
    parts.push(found.part);
    suites.push(found.suite);
    if (found.rest) kept.push(found.rest);
  }
  return { lines: kept, parts, suites };
}

/** The first suite part in a Location's lines, or null; what the refusal names. */
export function suitePartInLines(lines: readonly string[]): string | null {
  return splitSuiteFromLines(lines).parts[0] ?? null;
}

/**
 * 🔒 The sentence a Location's save is refused with when its lines carry a suite
 * part (decision 7400). It names the part and the field it goes in.
 */
export function suiteOnLocationRefusal(part: string): string {
  return fill(t('location.suiteRefusal'), { part });
}

/** What the Address Needs Review queue says beside a building whose lines still carry a suite part. */
export function suiteOnLocationQueueLine(part: string): string {
  return fill(t('review.suiteQueue'), { part });
}
